import IconButton from '~components/_common/IconButton/IconButton';
import { FileVersionSelection } from '~src/contexts/FileVersionsContext';
import FileCadIcon from '~src/assets/svf-icons/file-cad-regular-full.svg';
import AutodeskIcon from '~src/assets/svf-icons/autodesk.svg';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import SVFIconButton from '~components/_common/SVFViewerIconButton/SVFIconButton';

export type SVFIconTypeProps = {
  version?: FileVersionSelection;
  onClick: () => void;
  type: any;
  extension?: string;
  variant?: 'table' | 'sidebar';
  customTooltip?: string;
  disabled?: boolean;
  mobileView?: boolean;
};

const fileIconColorFiltersAlt: Record<string, string> = {
  dwg: 'brightness(0) saturate(100%) invert(50%) sepia(0%) saturate(0%)', // #808080 gray
  ifc:
    'brightness(0) saturate(100%) invert(16%) sepia(84%) saturate(4851%) hue-rotate(346deg)', // #dc143c red
  rvt:
    'brightness(0) saturate(100%) invert(19%) sepia(89%) saturate(1718%) hue-rotate(217deg)', // #0066cc blue
};
const statusColorFilters: Record<string, string> = {
  gray: 'invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.7)',
  blue:
    'invert(27%) sepia(96%) saturate(6144%) hue-rotate(205deg) brightness(1)',
  green:
    'invert(48%) sepia(79%) saturate(2476%) hue-rotate(55deg) brightness(1.2)',
  red:
    'invert(16%) sepia(99%) saturate(7404%) hue-rotate(4deg) brightness(1.1)',
};

const statusMapWithFilters: Record<
  number,
  { color: string; filter: string; tooltip: string }
> = {
  1: {
    color: 'blue',
    filter: statusColorFilters.blue,
    tooltip: 'Job created and queued',
  },
  2: {
    color: 'blue',
    filter: statusColorFilters.blue,
    tooltip: 'File transferring to Autodesk',
  },
  4: {
    color: 'blue',
    filter: statusColorFilters.blue,
    tooltip: 'SVF2 conversion in progress',
  },
  7: {
    color: 'green',
    filter: statusColorFilters.green,
    tooltip: 'Conversion successful',
  },
  5: {
    color: 'red',
    filter: statusColorFilters.red,
    tooltip: 'Conversion failed',
  },
};

export const SVFIconType = ({
  version,
  onClick,
  type,
  extension,
  variant = 'table',
  customTooltip,
  disabled,
  mobileView = false,
}: SVFIconTypeProps) => {
  // Case: version is not defined
  if (!version) {
    // console.log('[SVFIconType] render WITHOUT version', { extension, variant });
    // For CAD files (rvt, dwg, ifc), show blue CAD icon even without version data
    const isCadExtension = ['rvt', 'dwg', 'ifc'].includes(
      extension?.toLowerCase() || ''
    );
    if (isCadExtension) {
      if (variant === 'table') {
        // Right side (file table): Always show blue CAD icon
        return (
          <SVFIconButton
            tooltip={customTooltip}
            icon={
              <NavIcon
                icon={FileCadIcon}
                customStyle={{
                  height: 24,
                  width: 24,
                  filter: fileIconColorFiltersAlt.rvt, // Always blue for table
                  margin: 0,
                }}
              />
            }
            click={onClick}
          />
        );
      } else {
        // Left side (sidebar/context menu): Show gray Autodesk icon when no version data

        if (mobileView) return null; // hide gray/blue icons for mobile

        return (
          <SVFIconButton
            tooltip={disabled ? undefined : customTooltip}
            icon={
              <NavIcon
                icon={AutodeskIcon}
                customStyle={{
                  height: 24,
                  width: 24,
                  filter: statusColorFilters.gray, // Gray for unknown state
                  margin: 0,
                }}
              />
            }
            click={onClick}
            disabled={disabled}
          />
        );
      }
    }

    // For other files, show generic icon
    return (
      <IconButton
        click={onClick}
        iconStyle={type?.iconStyle}
        icon={['far', 'file-alt']}
        color={'gray'}
        disabled
      />
    );
  }

  // Case: SVF model exists - always show blue CAD icon regardless of state
  const state = version.extension_svf_model?.state;
  if (state != null && statusMapWithFilters[state]) {
    // console.log('[SVFIconType] render WITH state', {
    //   state,
    //   extension,
    //   variant,
    // });
    if (variant === 'table') {
      // Right side (file table): Always show blue CAD icon
      return (
        <SVFIconButton
          tooltip={customTooltip || statusMapWithFilters[state].tooltip}
          icon={
            <NavIcon
              icon={FileCadIcon}
              customStyle={{
                height: 24,
                width: 24,
                filter: fileIconColorFiltersAlt.rvt, // Always blue for table
                margin: 0,
              }}
            />
          }
          click={onClick}
        />
      );
    } else {
      // Left side (sidebar/context menu): Show Autodesk icon with color based on state

      if (mobileView && state !== 7) return null;

      return (
        <SVFIconButton
          tooltip={
            disabled
              ? undefined
              : customTooltip || statusMapWithFilters[state].tooltip
          }
          icon={
            <NavIcon
              icon={AutodeskIcon}
              customStyle={{
                height: 24,
                width: 24,
                filter: statusMapWithFilters[state].filter, // Color based on state
                margin: 0,
              }}
            />
          }
          click={onClick}
          disabled={disabled}
        />
      );
    }
  }

  // Default fallback icon - use blue CAD icon for Autodesk files
  if (variant === 'table') {
    // Right side (file table): Blue CAD icon
    return (
      <SVFIconButton
        tooltip={customTooltip}
        icon={
          <NavIcon
            icon={FileCadIcon}
            customStyle={{
              height: 24,
              width: 24,
              filter: fileIconColorFiltersAlt.rvt, // Always blue for table
              margin: 0,
            }}
          />
        }
        click={onClick}
      />
    );
  } else {
    // Left side (sidebar/context menu): Gray Autodesk icon
    // console.log('[SVFIconType] render DEFAULT fallback', {
    //   extension,
    //   variant,
    // });
    if (mobileView && state !== 7) return null;
    return (
      <SVFIconButton
        tooltip={disabled ? undefined : customTooltip}
        icon={
          <NavIcon
            icon={AutodeskIcon}
            customStyle={{
              height: 24,
              width: 24,
              filter: statusColorFilters.gray, // Gray for unknown state
              margin: 0,
            }}
          />
        }
        click={onClick}
      />
    );
  }
};
